import { PersonPropsSchema, PERSON_NATURAL_KEY_FIELD } from '../../graph-schemas/base-crm/person';
import { OrganizationPropsSchema, ORGANIZATION_NATURAL_KEY_FIELD } from '../../graph-schemas/base-crm/organization';
import { ActivityPropsSchema } from '../../graph-schemas/base-crm/activity';
import { TaskPropsSchema } from '../../graph-schemas/base-crm/task';
import { DocumentPropsSchema } from '../../graph-schemas/base-crm/document';

export interface ListViewInclude {
  edgeType: string;
  direction: 'outgoing' | 'incoming';
  nodeTypes: string[];
  as: string;
  includeEdgeProps?: boolean;
  edgeFilter?: Record<string, unknown>;
}

export interface LookupConfig {
  nodeType: string;
  edgeType: string;
  direction: 'outgoing' | 'incoming';
  edgeProps?: Record<string, unknown>;
}

export interface FormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'url' | 'number' | 'date' | 'datetime' | 'select' | 'textarea' | 'richtext' | 'json' | 'geo' | 'user' | 'lookup';
  required?: boolean;
  options?: string[];
  picklistName?: string;
  format?: 'currency' | 'percentage' | 'year';
  lookupConfig?: LookupConfig;
  newLine?: boolean;
}

export interface RelationshipSectionConfig {
  sectionLabel: string;
  icon: string;
  edgeTypes: string[];
  direction: 'outgoing' | 'incoming' | 'both';
  relatedNodeTypes?: string[];
  renderMode: 'card' | 'grid';
  primaryProp?: string;
  secondaryProps?: string[];
  columns?: Array<{
    field: string;
    headerName: string;
    flex?: number;
    width?: number;
    minWidth?: number;
    type?: 'string' | 'number' | 'date' | 'datetime' | 'boolean';
    format?: 'currency' | 'percentage' | 'year';
    source?: 'node' | 'edge';
    editable?: boolean;
    editType?: 'text' | 'date' | 'datetime' | 'number' | 'boolean' | 'select' | 'picklist';
    options?: string[];
    picklistName?: string;
  }>;
}

export interface NodeDefinition {
  type: string;
  schema: any;
  displayName: string;
  displayNamePlural: string;
  icon: string;
  color: string;
  description?: string;
  synonyms?: string[];
  formFields: FormFieldConfig[];
  pmnEnabled: boolean;
  hasAttachments?: boolean;
  hasThreads?: boolean;
  hasOrgChart?: boolean;
  naturalKeyField?: string;
  relationshipSections?: RelationshipSectionConfig[];
  /** Diggens keeps these records (an app type marked readOnly): the CRM offers no create, edit or delete, and the API refuses them. */
  readOnly?: boolean;
  listViewIncludes?: ListViewInclude[];
}

export const BASE_CRM_NODES: Record<string, NodeDefinition> = {
  Person: {
    type: 'Person',
    schema: PersonPropsSchema,
    displayName: 'Person',
    displayNamePlural: 'People',
    icon: 'User',
    color: '#3b82f6',
    description: 'Individual contacts including investors, LPs, founders, executives, and other people',
    synonyms: ['contact', 'investor', 'LP contact', 'investor contact', 'founder', 'executive', 'individual'],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'role_type', label: 'Role', type: 'select', picklistName: 'person_role_type' },
      { key: 'seniority', label: 'Seniority', type: 'select', picklistName: 'person_seniority' },
      { key: 'geo', label: 'Location', type: 'geo' },
    ],
    pmnEnabled: true,
    naturalKeyField: PERSON_NATURAL_KEY_FIELD,
    relationshipSections: [
      {
        sectionLabel: 'Companies',
        icon: 'Building2',
        edgeTypes: ['WORKS_AT'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Company', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'title', headerName: 'Title', flex: 1, minWidth: 100, source: 'edge', editable: true, editType: 'text' },
          { field: 'start_date', headerName: 'Start', width: 120, source: 'edge', editable: true, editType: 'date' },
          { field: 'end_date', headerName: 'End', width: 120, source: 'edge', editable: true, editType: 'date' },
        ],
      },
      {
        sectionLabel: 'Activities',
        icon: 'MessageSquare',
        edgeTypes: ['RELATED_TO'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 100, source: 'node', editable: false },
          { field: 'occurred_at', headerName: 'Date', width: 120, type: 'datetime', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Investment Accounts',
        icon: 'Wallet',
        edgeTypes: ['SIGNATORY_FOR'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Account', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 120, source: 'node', editable: false },
          { field: 'role', headerName: 'Role', width: 120, source: 'edge', editable: true, editType: 'select', options: ['primary', 'secondary', 'authorized_signer'] },
        ],
      },
      {
        sectionLabel: 'Commitments',
        icon: 'FileSignature',
        edgeTypes: ['MANAGES_COMMITMENT'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Commitment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'commitment_amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
          { field: 'role', headerName: 'Role', width: 120, source: 'edge', editable: true, editType: 'select', options: ['primary_contact', 'secondary_contact', 'authorized_signer'] },
        ],
      },
    ],
  },
  Organization: {
    type: 'Organization',
    schema: OrganizationPropsSchema,
    displayName: 'Organization',
    displayNamePlural: 'Organizations',
    icon: 'Building2',
    color: '#10b981',
    description: 'Companies, LPs, portfolio companies, and other organizations',
    synonyms: ['company', 'LP', 'limited partner', 'portfolio company', 'portco', 'firm', 'business'],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'website', label: 'Website', type: 'url' },
      { key: 'status', label: 'Status', type: 'select', picklistName: 'organization_status' },
      { key: 'organization_type', label: 'Type', type: 'select', picklistName: 'organization_type' },
      { key: 'industry', label: 'Industry', type: 'select', picklistName: 'industry' },
      { key: 'geo', label: 'Location', type: 'geo' },
    ],
    pmnEnabled: true,
    hasOrgChart: true,
    naturalKeyField: ORGANIZATION_NATURAL_KEY_FIELD,
    relationshipSections: [
      {
        sectionLabel: 'People',
        icon: 'Users',
        edgeTypes: ['WORKS_AT'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'title', headerName: 'Title', flex: 1, minWidth: 100, source: 'edge', editable: true, editType: 'text' },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Document Contacts',
        icon: 'FileText',
        edgeTypes: ['RESPONSIBLE_FOR_DOCUMENTS'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
          { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 120, source: 'edge', editable: true, editType: 'text' },
        ],
      },
      {
        sectionLabel: 'Opportunities',
        icon: 'BriefcaseBusiness',
        edgeTypes: ['OPPORTUNITY_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Opportunity', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'stage', headerName: 'Stage', width: 120, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Deals',
        icon: 'Briefcase',
        edgeTypes: ['DEAL_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Deal', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'stage', headerName: 'Stage', width: 120, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Investments',
        icon: 'TrendingUp',
        edgeTypes: ['INVESTMENT_IN'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Investment', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'amount', headerName: 'Amount', width: 120, type: 'number', format: 'currency', source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Investment Accounts',
        icon: 'Wallet',
        edgeTypes: ['ACCOUNT_FOR'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Account', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 120, source: 'node', editable: false },
          { field: 'status', headerName: 'Status', width: 120, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Activities',
        icon: 'MessageSquare',
        edgeTypes: ['RELATED_TO'],
        direction: 'incoming',
        renderMode: 'grid',
        columns: [
          { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 100, source: 'node', editable: false },
          { field: 'occurred_at', headerName: 'Date', width: 120, type: 'datetime', source: 'node', editable: false },
        ],
      },
    ],
  },
  Activity: {
    type: 'Activity',
    schema: ActivityPropsSchema,
    displayName: 'Activity',
    displayNamePlural: 'Activities',
    icon: 'MessageSquare',
    color: '#6366f1',
    formFields: [
      { key: 'subject', label: 'Subject', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', picklistName: 'activity_type' },
      { key: 'direction', label: 'Direction', type: 'select', options: ['inbound', 'outbound', 'bidirectional'] },
      { key: 'occurred_at', label: 'Date', type: 'datetime' },
      { key: 'summary', label: 'Summary', newLine: true, type: 'richtext' },
    ],
    pmnEnabled: false,
    hasAttachments: true,
    hasThreads: true,
    relationshipSections: [
      {
        sectionLabel: 'Related To',
        icon: 'Link',
        edgeTypes: ['RELATED_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
          { field: 'type', headerName: 'Type', flex: 1, minWidth: 200 },
        ],
      },
    ],
  },
  Task: {
    type: 'Task',
    schema: TaskPropsSchema,
    displayName: 'Task',
    displayNamePlural: 'Tasks',
    icon: 'CheckSquare',
    color: '#10b981',
    formFields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'task_status' },
      { key: 'due_date', label: 'Due Date', type: 'date' },
      { key: 'assigned_user_id', label: 'Assigned To', type: 'user' },
    ],
    pmnEnabled: false,
    relationshipSections: [
      {
        sectionLabel: 'Assigned To',
        icon: 'UserCheck',
        edgeTypes: ['ASSIGNED_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
        ],
      },
      {
        sectionLabel: 'Related To',
        icon: 'Link',
        edgeTypes: ['RELATED_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 120, source: 'node', editable: false },
        ],
      },
    ],
  },
  Document: {
    type: 'Document',
    schema: DocumentPropsSchema,
    displayName: 'Document',
    displayNamePlural: 'Documents',
    icon: 'FileText',
    color: '#6b7280',
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'type', label: 'Type', type: 'select', required: true, picklistName: 'document_type' },
      { key: 'status', label: 'Status', type: 'select', picklistName: 'document_status' },
      { key: 'url', label: 'URL', type: 'url' },
      { key: 'uploaded_at', label: 'Uploaded At', type: 'datetime' },
    ],
    pmnEnabled: false,
    relationshipSections: [
      {
        sectionLabel: 'Related To',
        icon: 'Link',
        edgeTypes: ['RELATED_TO'],
        direction: 'outgoing',
        renderMode: 'grid',
        columns: [
          { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
          { field: 'type', headerName: 'Type', width: 120, source: 'node', editable: false },
        ],
      },
    ],
  },
};
